// Panelist language layer — provides:
//   • current language ("en" | "ur")
//   • setLang(lang)  (persists to localStorage + user record)
//   • labels[key]    (the ~30 fixed UI strings, EN or UR based on lang)
//   • t(key)         (shorthand — safe fallback if key unknown)
//
// The provider auto-loads /api/site-content/labels once. Toggling language
// flips the `dir` attribute on <html> to `rtl` for Urdu, and loads the Noto
// Naskh Arabic font (lazy — only when Urdu is first activated).
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const LanguageCtx = createContext(null);

const LS_KEY = "oodel_lang_v1";
const LS_FEATURE_KEY = "oodel_urdu_feature_v1";  // cache the master toggle so we don't flash the pill on every page load

// Guaranteed fallback labels — used before the API responds and if a key is
// somehow missing from the server payload. Keep in sync with UI_LABEL_KEYS
// in backend/routers/i18n.py.
const FALLBACK_LABELS = {
  ui_ur_questions:       { en: "questions",       ur: "سوالات" },
  ui_ur_question:        { en: "question",        ur: "سوال" },
  ui_ur_minutes:         { en: "min",             ur: "منٹ" },
  ui_ur_points:          { en: "pts",             ur: "پوائنٹس" },
  ui_ur_start:           { en: "Start",           ur: "شروع کریں" },
  ui_ur_continue:        { en: "Continue →",      ur: "جاری رکھیں ←" },
  ui_ur_next:            { en: "Next",            ur: "اگلا" },
  ui_ur_back:            { en: "Back",            ur: "پیچھے" },
  ui_ur_submit:          { en: "Submit",          ur: "جمع کروائیں" },
  ui_ur_cancel:          { en: "Cancel",          ur: "منسوخ کریں" },
  ui_ur_thanks_title:    { en: "Thank you!",      ur: "شکریہ!" },
  ui_ur_you_earned:      { en: "You earned",      ur: "آپ نے کمائے" },
  ui_ur_earn:            { en: "earn",            ur: "کمائیں" },
  ui_ur_available_surveys:{ en: "Available Waves", ur: "دستیاب ویوز" },
  ui_ur_no_surveys:      { en: "No surveys available right now.", ur: "ابھی کوئی سروے دستیاب نہیں۔" },
  ui_ur_home:            { en: "Home",            ur: "ہوم" },
  ui_ur_dashboard:       { en: "Dashboard",       ur: "ڈیش بورڈ" },
  ui_ur_profile:         { en: "Profile",         ur: "پروفائل" },
  ui_ur_points_label:    { en: "Points",          ur: "پوائنٹس" },
  ui_ur_help:            { en: "Help",            ur: "مدد" },
  ui_ur_logout:          { en: "Log out",         ur: "لاگ آؤٹ" },
  ui_ur_your_answer:     { en: "Your answer",     ur: "آپ کا جواب" },
  ui_ur_select_all:      { en: "Select all that apply", ur: "جتنے قابل اطلاق ہیں منتخب کریں" },
  ui_ur_required:        { en: "Required",        ur: "لازمی" },
  ui_ur_of:              { en: "of",              ur: "میں سے" },
  ui_ur_screened_msg:    { en: "This survey has reached its participant quota.", ur: "یہ سروے اپنی مطلوبہ تعداد مکمل کر چکا ہے۔" },
  ui_ur_completed_msg:   { en: "Thanks for completing this survey!", ur: "سروے مکمل کرنے کا شکریہ!" },
  // Thanks / post-submit screen
  ui_ur_survey_submitted:{ en: "Survey submitted", ur: "سروے جمع کرا دیا گیا" },
  ui_ur_response_recorded:{ en: "Your response to {name} has been recorded.",
                            ur: "{name} کے لیے آپ کا جواب ریکارڈ کر لیا گیا ہے۔" },
  ui_ur_close_arrow:     { en: "Close →", ur: "بند کریں ←" },
  ui_ur_redirect_notice: { en: "You will be taken to your dashboard",
                            ur: "آپ کو آپ کے ڈیش بورڈ پر لے جایا جائے گا" },
  // Already-completed survey screen (patron re-opens a wave they've submitted)
  ui_ur_already_pill:    { en: "SUBMITTED · THANK YOU",
                            ur: "جمع ہو چکا · شکریہ" },
  ui_ur_already_title:   { en: "You've already completed this survey.",
                            ur: "آپ یہ سروے پہلے ہی مکمل کر چکے ہیں۔" },
  ui_ur_already_recorded:{ en: "{name} — your response was recorded and the points have been credited to your account.",
                            ur: "{name} — آپ کا جواب ریکارڈ کر لیا گیا ہے اور پوائنٹس آپ کے اکاؤنٹ میں جمع کر دیے گئے ہیں۔" },
  ui_ur_already_integrity:{ en: "We never accept a second submission for the same wave — that's how we keep results clean and trustworthy.",
                            ur: "ہم ایک ہی سروے میں دوبارہ جواب قبول نہیں کرتے — اسی سے نتائج معتبر اور شفاف رہتے ہیں۔" },
  ui_ur_back_to_dashboard:{ en: "Back to dashboard", ur: "ڈیش بورڈ پر واپس" },
  ui_ur_view_completed:  { en: "View completed surveys",
                            ur: "مکمل شدہ سروے دیکھیں" },
  // Navigation
  ui_ur_nav_home:        { en: "Home",       ur: "ہوم" },
  ui_ur_nav_waves:       { en: "Waves",      ur: "ویوز" },
  ui_ur_nav_trackers:    { en: "Trackers",   ur: "ٹریکرز" },
  ui_ur_nav_snapshots:   { en: "Snapshots",  ur: "اسنیپ شاٹس" },
  ui_ur_nav_articles:    { en: "Articles",   ur: "مضامین" },
  ui_ur_nav_sentiment:   { en: "Sentiment",  ur: "رائے عامہ" },
  ui_ur_nav_watchlist:   { en: "Watchlist",  ur: "واچ لسٹ" },
  ui_ur_nav_rewards:     { en: "Rewards",    ur: "انعامات" },
  ui_ur_nav_profile:     { en: "Profile",    ur: "پروفائل" },
  ui_ur_nav_ambassador:  { en: "Ambassador", ur: "ایمبیسیڈر" },
  ui_ur_nav_dashboard:   { en: "Dashboard",  ur: "ڈیش بورڈ" },
  ui_ur_nav_help:        { en: "Help",       ur: "مدد" },
  ui_ur_signed_in_as:    { en: "Signed in as", ur: "بطور صارف لاگ ان" },
  ui_ur_sign_out:        { en: "Sign out",   ur: "سائن آؤٹ" },
  // Intro screen
  ui_ur_before_you_start:{ en: "Before you start", ur: "شروع کرنے سے پہلے" },
  ui_ur_intro_expected:  { en: "Intro · expected ~{minutes} min",
                            ur: "تعارف · متوقع ~{minutes} منٹ" },
  // Referral card
  ui_ur_referral_headline:  { en: "Invite a friend · Earn +{points} pts",
                              ur: "دوست کو دعوت دیں · {points}+ پوائنٹس کمائیں" },
  ui_ur_referral_your_code: { en: "Your code:", ur: "آپ کا کوڈ:" },
  ui_ur_referral_explainer: { en: "When a friend signs up with your code and completes their first survey, you get +{points} points.",
                              ur: "جب آپ کا دوست آپ کے کوڈ کے ساتھ سائن اپ کر کے اپنا پہلا سروے مکمل کرے گا تو آپ کو {points}+ پوائنٹس ملیں گے۔" },
  ui_ur_referral_invited:   { en: "Invited",   ur: "دعوت دی گئی" },
  ui_ur_referral_credited:  { en: "Credited",  ur: "کریڈٹ ہوئی" },
  ui_ur_referral_pending:   { en: "Pending",   ur: "زیرِ التوا" },
  ui_ur_referral_earned:    { en: "You've earned {points} pts from referrals",
                              ur: "آپ نے حوالہ جات سے {points} پوائنٹس کمائے ہیں" },
  ui_ur_copy_share_link:    { en: "Copy share link",    ur: "شیئر لنک کاپی کریں" },
  ui_ur_share_on_whatsapp:  { en: "Share on WhatsApp",  ur: "واٹس ایپ پر شیئر کریں" },
  ui_ur_share_link_copied:  { en: "Share link copied!", ur: "شیئر لنک کاپی ہو گیا!" },
  ui_ur_couldnt_copy:       { en: "Couldn't copy.",     ur: "کاپی نہیں ہو سکا۔" },
  // ITER 67 — Snapshot detail (crosstabs) page + article chart overlines.
  ui_ur_snapshot_overline:   { en: "SNAPSHOT",           ur: "اسنیپ شاٹ" },
  ui_ur_overall_distribution:{ en: "OVERALL DISTRIBUTION", ur: "مجموعی تقسیم" },
  ui_ur_sample_size:         { en: "Sample size N =",    ur: "= N نمونے کی تعداد" },
  ui_ur_crosstab:            { en: "CROSSTAB",           ur: "کراس ٹیب" },
  ui_ur_group:               { en: "Group",              ur: "گروپ" },
  ui_ur_back_to_wave:        { en: "← back to wave",     ur: "→ ویو پر واپس" },
  ui_ur_all_waves:           { en: "← All waves",        ur: "→ تمام ویوز" },
  ui_ur_chart:               { en: "CHART",              ur: "چارٹ" },
  ui_ur_wave_result:         { en: "WAVE RESULT",        ur: "ویو نتیجہ" },
  ui_ur_tracker:             { en: "TRACKER",            ur: "ٹریکر" },
  ui_ur_no_data:             { en: "No data published for this question yet.",
                               ur: "اس سوال کے لیے ابھی تک کوئی ڈیٹا شائع نہیں ہوا۔" },
  ui_ur_no_tracker_data:     { en: "No tracker data available.", ur: "ٹریکر ڈیٹا دستیاب نہیں۔" },
  ui_ur_no_snapshot_data:    { en: "No snapshot data available.", ur: "اسنیپ شاٹ ڈیٹا دستیاب نہیں۔" },
  ui_ur_cat_gender:          { en: "GENDER",             ur: "جنس" },
  ui_ur_cat_age:             { en: "AGE",                ur: "عمر" },
  ui_ur_cat_region:          { en: "REGION",             ur: "علاقہ" },
  ui_ur_cat_sec:             { en: "SEC",                ur: "سماجی-معاشی طبقہ" },
  ui_ur_val_male:            { en: "Male",               ur: "مرد" },
  ui_ur_val_female:          { en: "Female",             ur: "خاتون" },
  ui_ur_val_other:           { en: "Other",              ur: "دیگر" },
  ui_ur_val_punjab:          { en: "Punjab",             ur: "پنجاب" },
  ui_ur_val_sindh:           { en: "Sindh",              ur: "سندھ" },
  ui_ur_val_kpk:             { en: "KPK",                ur: "کے پی کے" },
  ui_ur_val_kp:              { en: "KP",                 ur: "کے پی" },
  ui_ur_val_balochistan:     { en: "Balochistan",        ur: "بلوچستان" },
  ui_ur_val_islamabad:       { en: "Islamabad",          ur: "اسلام آباد" },
  ui_ur_val_ajk:             { en: "AJK",                ur: "آزاد کشمیر" },
  ui_ur_val_gb:              { en: "GB",                 ur: "گلگت-بلتستان" },
  ui_ur_val_18_24:           { en: "18-24",              ur: "24-18" },
  ui_ur_val_25_34:           { en: "25-34",              ur: "34-25" },
  ui_ur_val_35_44:           { en: "35-44",              ur: "44-35" },
  ui_ur_val_45_54:           { en: "45-54",              ur: "54-45" },
  ui_ur_val_55_plus:         { en: "55+",                ur: "+55" },
  ui_ur_val_sec_a:           { en: "SEC A",              ur: "طبقہ A" },
  ui_ur_val_sec_b:           { en: "SEC B",              ur: "طبقہ B" },
  ui_ur_val_sec_c:           { en: "SEC C",              ur: "طبقہ C" },
  ui_ur_val_sec_d:           { en: "SEC D",              ur: "طبقہ D" },
  ui_ur_val_sec_e:           { en: "SEC E",              ur: "طبقہ E" },
  // Snapshot detail paywall (for anonymous visitors)
  ui_ur_snap_pw_pill:        { en: "SIGN UP TO UNLOCK", ur: "غیر مقفل کرنے کے لیے سائن اَپ کریں" },
  ui_ur_snap_pw_title:       { en: "Full crosstabs are for oodel. panelists",
                               ur: "مکمل کراس ٹیبس صرف oodel. پینلسٹس کے لیے ہیں" },
  ui_ur_snap_pw_body:        { en: "Sign in or join the oodel. panel — it takes 60 seconds — to view respondent breakdowns by region, age, and gender.",
                               ur: "علاقے، عمر اور جنس کے حساب سے جواب دہندگان کی تقسیم دیکھنے کے لیے سائن ان کریں یا oodel. پینل میں شامل ہوں — صرف 60 سیکنڈ لگتے ہیں۔" },
  ui_ur_signup_arrow:        { en: "Sign up →",        ur: "→ سائن اَپ کریں" },
  ui_ur_signin:              { en: "Sign in",          ur: "سائن ان" },
  // ITER 97 — Patron dashboard redesign strings, kept in sync with
  // UI_LABEL_KEYS in backend/routers/i18n.py.
  ui_ur_profile_incomplete_overline: { en: "PROFILE INCOMPLETE", ur: "پروفائل نامکمل" },
  ui_ur_profile_incomplete_title: { en: "Complete your profile to start taking surveys",
                                     ur: "سروے شروع کرنے کے لیے اپنا پروفائل مکمل کریں" },
  ui_ur_profile_incomplete_body: { en: "Every field is required and you must pick at least 3 areas of interest. This keeps oodel's data nationally representative — without it, your responses can't be weighted.",
                                    ur: "ہر فیلڈ لازمی ہے اور آپ کو کم از کم 3 دلچسپی کے شعبے منتخب کرنا ہوں گے۔ اس سے oodel کا ڈیٹا قومی سطح پر نمائندہ رہتا ہے — اس کے بغیر آپ کے جوابات کو وزن نہیں دیا جا سکتا۔" },
  ui_ur_complete_profile_now: { en: "Complete profile now →", ur: "ابھی پروفائل مکمل کریں ←" },
  ui_ur_complete_profile_arrow: { en: "Complete profile →", ur: "پروفائل مکمل کریں ←" },
  ui_ur_congratulations:     { en: "CONGRATULATIONS",  ur: "مبارک ہو" },
  ui_ur_raffle_win_heading:  { en: "You have a raffle win to celebrate.", ur: "آپ کی ریفل جیت کا جشن منانے کا وقت ہے۔" },
  ui_ur_raffle_winner_pill:  { en: "🎉 RAFFLE WINNER",  ur: "🎉 ریفل فاتح" },
  ui_ur_raffle_win_title:    { en: "You've won the oodel. raffle.", ur: "آپ نے oodel. ریفل جیت لیا ہے۔" },
  ui_ur_draw_label:          { en: "Draw:",            ur: "ڈرا:" },
  ui_ur_drawn_on:            { en: "drawn",            ur: "تاریخِ ڈرا" },
  ui_ur_raffle_claim_notice: { en: "Our team will be in touch shortly with claim details. No action needed from you right now.",
                                ur: "ہماری ٹیم جلد ہی کلیم کی تفصیلات کے ساتھ رابطہ کرے گی۔ ابھی آپ کو کچھ کرنے کی ضرورت نہیں۔" },
  ui_ur_got_it:              { en: "Got it",           ur: "سمجھ گیا" },
  ui_ur_dismiss:             { en: "Dismiss",          ur: "نظر انداز کریں" },
  ui_ur_revote_overline:     { en: "PUBLIC SENTIMENT · RE-RATE WINDOW", ur: "عوامی رائے · دوبارہ درجہ بندی" },
  ui_ur_revote_title:        { en: "Your view, then and now.", ur: "آپ کی رائے، پہلے اور اب۔" },
  ui_ur_revote_ready:        { en: "{n} item(s) ready for a fresh rating.", ur: "{n} آئٹمز نئی درجہ بندی کے لیے تیار ہیں۔" },
  ui_ur_revote_upcoming:     { en: "Coming up: items you can re-rate soon.", ur: "جلد آنے والا: وہ آئٹمز جن کی آپ دوبارہ درجہ بندی کر سکیں گے۔" },
  ui_ur_revote_rerate:       { en: "Re-rate",          ur: "دوبارہ درجہ دیں" },
  ui_ur_revote_rated:        { en: "You rated {stars}★ · {days} days ago", ur: "آپ نے {stars}★ درجہ دیا · {days} دن پہلے" },
  ui_ur_revote_eligible_in:  { en: "Eligible in {days} day(s)", ur: "{days} دن میں اہل ہوں گے" },
  ui_ur_stat_surveys_completed: { en: "Surveys Completed", ur: "مکمل شدہ سروے" },
  ui_ur_stat_lifetime_points: { en: "Lifetime Points",  ur: "مجموعی پوائنٹس" },
  ui_ur_stat_referrals:      { en: "Referrals",        ur: "حوالہ جات" },
  ui_ur_stat_hint_keep_it_up: { en: "Keep it up!",      ur: "ایسے ہی جاری رکھیں!" },
  ui_ur_stat_hint_view_history: { en: "View history",   ur: "تاریخ دیکھیں" },
  ui_ur_stat_hint_invite_earn: { en: "Invite & earn more", ur: "دعوت دیں اور مزید کمائیں" },
  ui_ur_current_wave:        { en: "Featured Wave",     ur: "نمایاں ویو" },
  ui_ur_latest_article:      { en: "Featured Article",   ur: "نمایاں مضمون" },
  ui_ur_read_editorial:      { en: "Read Editorial",   ur: "مضمون پڑھیں" },
  ui_ur_current_wave_fallback_desc: { en: "Share your views and help shape the next oodel. report.",
                                       ur: "اپنی رائے شیئر کریں اور oodel. کی اگلی رپورٹ کی تشکیل میں مدد کریں۔" },
  ui_ur_closes_today:        { en: "Closing today",   ur: "آج بند ہو رہا ہے" },
  ui_ur_closes_in_days:      { en: "Closes in {days} day(s)", ur: "{days} دن میں بند ہو جائے گا" },
  ui_ur_no_wave_available:   { en: "No survey available right now", ur: "ابھی کوئی سروے دستیاب نہیں" },
  ui_ur_no_wave_notice:      { en: "We'll notify you the moment a relevant one goes live.", ur: "متعلقہ سروے آتے ہی ہم آپ کو مطلع کریں گے۔" },
  ui_ur_no_articles_yet:     { en: "No reports published yet", ur: "ابھی تک کوئی رپورٹ شائع نہیں ہوئی" },
  ui_ur_completed_surveys:   { en: "Completed Waves", ur: "مکمل شدہ ویوز" },
  ui_ur_welcome_panel:       { en: "Welcome to the panel", ur: "پینل میں خوش آمدید" },
  ui_ur_no_match_profile:    { en: "No surveys match your profile right now — but the moment a relevant one goes live, we'll email you and drop a notification in your bell. You'll be one of the first to know.",
                                ur: "ابھی آپ کے پروفائل سے ملنے والا کوئی سروے موجود نہیں — لیکن متعلقہ سروے آتے ہی ہم آپ کو ای میل اور نوٹیفیکیشن بھیجیں گے۔ آپ سب سے پہلے جاننے والوں میں ہوں گے۔" },
  ui_ur_sharpen_profile:     { en: "Sharpen your profile", ur: "اپنا پروفائل بہتر بنائیں" },
  ui_ur_sharpen_profile_body: { en: "You can only take surveys once your profile is complete. The more we know about your sectors of interest, the better we can match you with relevant surveys.",
                                 ur: "آپ صرف اس وقت سروے لے سکتے ہیں جب آپ کا پروفائل مکمل ہو۔ ہم آپ کے دلچسپی کے شعبوں کے بارے میں جتنا زیادہ جانیں گے، اتنا ہی بہتر طریقے سے متعلقہ سروے سے آپ کو ملا سکیں گے۔" },
  ui_ur_no_completed_yet:    { en: "You haven't completed any surveys yet.", ur: "آپ نے ابھی تک کوئی سروے مکمل نہیں کیا۔" },
  ui_ur_badge_voided:        { en: "VOIDED",           ur: "منسوخ" },
  ui_ur_badge_screened:      { en: "SCREENED",         ur: "اسکرین شدہ" },
  ui_ur_badge_done:          { en: "✓ DONE",           ur: "✓ مکمل" },
  ui_ur_complete_profile_toast: { en: "Complete your profile (incl. 3+ areas of interest) first.",
                                   ur: "پہلے اپنا پروفائل مکمل کریں (کم از کم 3 دلچسپی کے شعبوں سمیت)۔" },
  ui_ur_latest_snapshot:     { en: "LATEST SNAPSHOT",  ur: "تازہ ترین سنیپ شاٹ" },
  ui_ur_latest_tracker:      { en: "LATEST TRACKER",   ur: "تازہ ترین ٹریکر" },
  ui_ur_latest_report:       { en: "LATEST REPORT",    ur: "تازہ ترین رپورٹ" },
  ui_ur_view_all_arrow:      { en: "View all →",       ur: "سب دیکھیں ←" },
  ui_ur_founding_patron:     { en: "FOUNDING PATRON",  ur: "بانی سرپرست" },
  ui_ur_campus_ambassador:   { en: "CAMPUS AMBASSADOR", ur: "کیمپس ایمبیسیڈر" },
  ui_ur_collapse:            { en: "Collapse",         ur: "سکیڑیں" },
  ui_ur_status_voided:       { en: "Voided · points reversed · reason: {reason}", ur: "منسوخ · پوائنٹس واپس · وجہ: {reason}" },
  ui_ur_status_screened:     { en: "Screened out · +{points} compensation", ur: "اسکرین آؤٹ · +{points} معاوضہ" },
  ui_ur_status_completed:    { en: "Completed · +{points} awarded", ur: "مکمل · +{points} حاصل ہوئے" },
  ui_ur_void_reason_default: { en: "response invalidated", ur: "جواب کالعدم قرار دیا گیا" },
  ui_ur_void_reason_fast_fill: { en: "completed too quickly", ur: "بہت جلدی مکمل کیا گیا" },
  ui_ur_void_reason_straight_line: { en: "same answer to every question", ur: "ہر سوال کا ایک ہی جواب" },
  ui_ur_void_reason_fraud:   { en: "quality / fraud flag", ur: "کوالٹی / فراڈ کی نشاندہی" },
  ui_ur_void_reason_manual:  { en: "manually invalidated by admin", ur: "ایڈمن نے دستی طور پر کالعدم کیا" },
  ui_ur_insight:             { en: "insight",          ur: "بصیرت" },
  ui_ur_insights:            { en: "insights",         ur: "بصیرتیں" },
  ui_ur_updated:             { en: "Updated",          ur: "تازہ کاری" },
  ui_ur_raffle_eligible:     { en: "🎯 You're eligible for the next raffle draw! Current balance: {have} pts.", ur: "🎯 آپ اگلے ریفل ڈرا کے لیے اہل ہیں! موجودہ بیلنس: {have} پوائنٹس۔" },
  ui_ur_raffle_need_more:    { en: "You're {need} pts away from raffle eligibility ({threshold} pts required).", ur: "آپ ریفل کی اہلیت سے {need} پوائنٹس دور ہیں ({threshold} پوائنٹس درکار ہیں)۔" },
  ui_ur_loading:             { en: "Loading…",         ur: "لوڈ ہو رہا ہے…" },
  // oodel score — public QR feedback form (CustomerSentimentRate)
  ui_ur_cs_rate_title:       { en: "Rate your experience", ur: "اپنے تجربے کی درجہ بندی کریں" },
  ui_ur_cs_rate_subtitle:    { en: "Takes less than 30 seconds — your feedback helps them improve.",
                                ur: "30 سیکنڈ سے کم وقت لگتا ہے — آپ کی رائے انہیں بہتر بنانے میں مدد دیتی ہے۔" },
  ui_ur_cs_email_label:      { en: "Email", ur: "ای میل" },
  ui_ur_cs_age_label:        { en: "Age", ur: "عمر" },
  ui_ur_cs_gender_label:     { en: "Gender", ur: "جنس" },
  ui_ur_cs_select_placeholder: { en: "Select", ur: "منتخب کریں" },
  ui_ur_cs_consent_text:     { en: "I consent to share my feedback with {business_name} and join the oodel community.",
                                ur: "میں {business_name} کے ساتھ اپنی رائے شیئر کرنے اور oodel کمیونٹی میں شامل ہونے پر رضامند ہوں۔" },
  ui_ur_cs_marketing_optin_text: { en: "Keep me updated on special offers and news from {business_name}. (Optional)",
                                ur: "مجھے {business_name} کی خصوصی آفرز اور خبروں سے آگاہ رکھیں۔ (اختیاری)" },
  ui_ur_cs_submit_label:     { en: "Submit feedback", ur: "رائے جمع کروائیں" },
  ui_ur_cs_submitting_label: { en: "Submitting…", ur: "جمع ہو رہا ہے…" },
  ui_ur_cs_thank_you_title:  { en: "Thank you!", ur: "شکریہ!" },
  ui_ur_cs_thank_you_message:{ en: "Your feedback has been submitted to", ur: "آپ کی رائے جمع کر دی گئی ہے" },
  ui_ur_cs_account_created_note: { en: "Check your inbox — we've sent a link to set your password and claim your free oodel account.",
                                     ur: "اپنا انباکس دیکھیں — ہم نے پاس ورڈ سیٹ کرنے اور اپنا مفت oodel اکاؤنٹ حاصل کرنے کا لنک بھیج دیا ہے۔" },
  ui_ur_cs_powered_by:       { en: "Powered by", ur: "کی جانب سے" },
  ui_ur_cs_link_used_title:  { en: "This link has already been used", ur: "یہ لنک پہلے ہی استعمال ہو چکا ہے" },
  ui_ur_cs_link_used_message:{ en: "Please scan the QR code again to leave feedback.", ur: "رائے دینے کے لیے براہ کرم دوبارہ QR کوڈ اسکین کریں۔" },
  ui_ur_cs_no_token_message: { en: "Please scan the oodel QR code displayed at the business to submit your feedback.",
                                ur: "براہ کرم اپنی رائے جمع کروانے کے لیے کاروبار پر موجود oodel QR کوڈ اسکین کریں۔" },
  ui_ur_cs_suspended_title:  { en: "This listing is temporarily paused", ur: "یہ لسٹنگ عارضی طور پر روک دی گئی ہے" },
  ui_ur_cs_suspended_message:{ en: "This business's oodel SCORE listing is temporarily unavailable. Please check back later.",
                                ur: "اس کاروبار کی oodel SCORE لسٹنگ عارضی طور پر دستیاب نہیں ہے۔ براہ کرم بعد میں دوبارہ چیک کریں۔" },
  ui_ur_cs_email_placeholder:{ en: "you@example.com", ur: "you@example.com" },
  ui_ur_cs_gender_male:     { en: "Male", ur: "مرد" },
  ui_ur_cs_gender_female:   { en: "Female", ur: "عورت" },
  ui_ur_cs_gender_other:   { en: "Other", ur: "دیگر" },
};

// Guarantee Noto Naskh Arabic is loaded exactly once when the user first
// switches to Urdu. We keep it off the critical path when the app boots in
// English (99% of visitors) so the initial paint stays fast.
let _urduFontLoaded = false;
function ensureUrduFontLoaded() {
  if (_urduFontLoaded) return;
  _urduFontLoaded = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || "en"; } catch { return "en"; }
  });
  const [labels, setLabels] = useState(FALLBACK_LABELS);
  // Master feature toggle (Admin → Site Content → Feature toggles →
  // urdu_feature_enabled). When false, we force English regardless of the
  // patron's saved preference and hide the EN/اردو pill everywhere.
  //
  // Cache the last-known value in localStorage so we don't flash the pill
  // on every page load. If the admin flipped the toggle since our last
  // fetch, the effect below will correct the value within one paint.
  // Default (first visit ever) is FALSE — safer to hide until confirmed,
  // otherwise a user whose admin has the feature OFF still sees the pill
  // briefly on every fresh device / private-window load.
  const [featureEnabled, setFeatureEnabled] = useState(() => {
    try {
      const cached = localStorage.getItem(LS_FEATURE_KEY);
      if (cached === "true") return true;
      if (cached === "false") return false;
    } catch {}
    return false;   // conservative default until API confirms
  });

  useEffect(() => {
    // Fetch once immediately, then keep the master toggle + labels in sync
    // with the admin's live edits: poll every 60s AND refetch whenever the
    // user's tab becomes visible or gains focus. This is what makes the
    // EN/اردو pill (and the labels themselves) appear/disappear on already-
    // open tabs after an admin flips the master switch — without any hard
    // reload from the patron.
    let mounted = true;
    const refreshFeatureFlag = () => {
      api.get("/site-content").then((r) => {
        if (!mounted) return;
        const raw = r?.data?.urdu_feature_enabled;
        const on = raw === undefined || raw === null
          ? true                                          // default ON when missing
          : String(raw).toLowerCase() !== "false";
        setFeatureEnabled(on);
        try { localStorage.setItem(LS_FEATURE_KEY, on ? "true" : "false"); } catch {}
      }).catch(() => {});
    };
    const refreshLabels = () => {
      api.get("/site-content/labels")
        .then((r) => {
          if (!mounted) return;
          if (r?.data && typeof r.data === "object") {
            setLabels({ ...FALLBACK_LABELS, ...r.data });
          }
        })
        .catch(() => {});
    };
    refreshFeatureFlag();
    refreshLabels();
    const intervalId = setInterval(refreshFeatureFlag, 60_000);
    const onVisibility = () => { if (document.visibilityState === "visible") refreshFeatureFlag(); };
    const onFocus = () => refreshFeatureFlag();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      mounted = false;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Effective language — forced to English when the master toggle is OFF.
  const effectiveLang = featureEnabled ? lang : "en";

  useEffect(() => {
    if (effectiveLang === "ur") {
      ensureUrduFontLoaded();
      document.documentElement.setAttribute("lang", "ur");
      document.body.classList.add("oodel-lang-ur");
    } else {
      document.documentElement.setAttribute("lang", "en");
      document.body.classList.remove("oodel-lang-ur");
    }
    document.documentElement.setAttribute("dir", "ltr");
  }, [effectiveLang]);

  const setLang = useCallback(async (next) => {
    const clean = next === "ur" ? "ur" : "en";
    setLangState(clean);
    try { localStorage.setItem(LS_KEY, clean); } catch {}
    // Best-effort persist on the user record so the choice survives
    // device swaps + follows the patron to email links. Anonymous
    // visitors (no auth token) have no user record to persist to —
    // skip the call entirely rather than firing a guaranteed 401.
    try {
      if (localStorage.getItem("pollpak_token")) {
        await api.put("/me/preferred-language", { language: clean });
      }
    } catch {}
  }, []);

  const t = useCallback(
    (key) => {
      const row = labels[key] || FALLBACK_LABELS[key];
      if (!row) return key;
      return (effectiveLang === "ur" ? row.ur : row.en) || row.en || key;
    },
    [labels, effectiveLang],
  );

  return (
    <LanguageCtx.Provider value={{ lang: effectiveLang, setLang, labels, t, featureEnabled }}>
      {children}
    </LanguageCtx.Provider>
  );
}

export function useLanguage() {
  const v = useContext(LanguageCtx);
  // If the provider isn't mounted (e.g. admin routes), fall back to English
  // so every screen that reaches for `t()` still renders correctly.
  if (!v) return { lang: "en", setLang: () => {}, labels: FALLBACK_LABELS, t: (k) => (FALLBACK_LABELS[k]?.en || k), featureEnabled: true };
  return v;
}
